import React, { useEffect, useState } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '../../theme/theme-provider';
import MusicPlayer from '../player/MusicPlayer';
import ThemeAmbientOverlay from '../theme-ambient-overlay/ThemeAmbientOverlay';
import BackToTopButton from '../back-to-top-button/BackToTopButton';
import AvatarPhoto from '../../static/images/photo1704392396_2_1.jpeg';
import PortraitPhoto from '../../static/images/20210409_033454-01-01-01-03-01.jpeg';
import { API } from '../../interceptors/axios';
import { fetchAuth } from '../../utils/auth';
import { getServiceToken } from '../../utils/encryption';

const REEL_SLUG = 'distorise-audio_reel_brands';

const rescoringProjects = [
  {
    title: 'Esper Bionics',
    description:
      'A minimal electronic approach inspired by the intersection of technology, fashion, and human-centered innovation. The focus was on creating a sound that feels modern, refined, and distinctly human.',
    videoId: 'VpMwwghsQvQ'
  },
  {
    title: 'Phantom Project + M-Tac',
    description:
      'A cinematic and atmospheric score built around resilience, preparedness, and determination. Ambient textures and subtle tension create a sense of focus without overpowering the story.',
    videoId: '5hL_k5WE6nk'
  },
  {
    title: 'PlayStation 5',
    description:
      'A modern electronic score designed to emphasize movement, interaction, and excitement. The music follows the pace of the visuals while maintaining a strong sense of energy and momentum.',
    videoId: '8tipwx5kf_Y'
  },
  {
    title: 'National Geographic',
    description:
      'A cinematic composition centered on curiosity, exploration, and discovery. The goal was to support the emotional impact of the visuals while preserving a sense of wonder and openness.',
    videoId: '8ueGfP1gizI'
  }
];

const VideoEmbed = ({ videoId, title, isDark }) => (
  <Box
    sx={{
      width: '100%',
      position: 'relative',
      paddingTop: '56.25%',
      borderRadius: 2,
      overflow: 'hidden',
      border: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.1)',
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.58)' : 'rgba(245, 245, 245, 0.88)',
      boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.25)' : '0 10px 24px rgba(0,0,0,0.12)'
    }}
  >
    <Box
      component="iframe"
      src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&enablejsapi=0`}
      title={title}
      loading="lazy"
      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0
      }}
    />
  </Box>
);

const WorkForBrands = ({ sendPage }) => {
  const { themeMode } = useTheme();
  const isDark = themeMode === 'dark';
  const accentGold = isDark ? '#F2D399' : '#1f1f1f';
  const headerColor = isDark ? 'white' : 'rgba(22,22,22,0.94)';
  const headerShadow = isDark ? '4px 4px black' : '2px 2px rgba(255,255,255,0.65)';
  const bodyColor = isDark ? 'white' : '#1a1a1a';

  const [reel, setReel] = useState(null);
  const [reelLoading, setReelLoading] = useState(true);
  const [reelStreamUrl, setReelStreamUrl] = useState(null);
  const [reelImageUrl, setReelImageUrl] = useState(null);

  const buildReelStreamUrl = async (reelId) => {
    const tokenRes = await API.get(
      `${process.env.REACT_APP_API_URL}/reel-stream-token/${reelId}`,
      { withCredentials: true }
    );
    const streamToken = tokenRes.data.token;
    const url = new URL(`${process.env.REACT_APP_API_URL}/stream-reel/${reelId}`);
    url.searchParams.set('token', streamToken);
    url.searchParams.set('x-service-token', getServiceToken());
    return { url: url.toString(), headers: { 'Accept': 'audio/mpeg' } };
  };

  useEffect(() => {
    sendPage('brands');
    window.scrollTo(0, 0);

    let cancelled = false; // guard against React StrictMode double-invocation

    const fetchReelData = async () => {
      try {
        await fetchAuth();
        if (cancelled) return;
        const res = await API.get(`${process.env.REACT_APP_API_URL}/reels/${REEL_SLUG}`);
        if (cancelled) return;
        const data = res.data;
        setReel(data);
        const reelFileId = data.reel ?? data.id;
        if (reelFileId) {
          // Pre-warm: download the reel into proxy memory before the user clicks play
          API.get(`${process.env.REACT_APP_API_URL}/warm-reel/${reelFileId}`).catch(() => {});
          const streamUrl = await buildReelStreamUrl(reelFileId);
          if (cancelled) return;
          setReelStreamUrl(streamUrl);
          setReelImageUrl(`${process.env.REACT_APP_API_URL}/public-reel-image/${reelFileId}`);
        }
      } catch (err) {
        console.error('Failed to load reel:', err);
      } finally {
        if (!cancelled) setReelLoading(false);
      }
    };

    fetchReelData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: 'var(--background-color)',
        minHeight: '100vh',
        pt: { xs: 2, md: 2 },
        pb: { xs: 6, md: 8 },
        position: 'relative',
        overflowX: 'hidden',
        overflowY: 'visible'
      }}
    >
      <ThemeAmbientOverlay speed={30} />
      <BackToTopButton />
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Hero title */}
        <Typography
          align="center"
          variant="h1"
          color={headerColor}
          sx={{
            fontFamily: 'Light-1',
            textShadow: headerShadow,
            fontSize: { xs: '1.6rem', sm: '2.1rem', md: '2.6rem', lg: '3rem' },
            lineHeight: 1.15,
            letterSpacing: '0.04em',
            mb: 5,
          }}
        >
          MUSIC THAT MAKES YOUR BRAND SOUND LIKE THE FUTURE
        </Typography>

        {/* Avatar + hero description — just text, no avatar here */}
        <Typography
          color={bodyColor}
          align="center"
          sx={{
            fontFamily: '"Courier New", monospace',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.18rem' },
            lineHeight: 1.65,
            fontWeight: 600,
            mb: 6,
          }}
        >
          Original scores, full sonic identities for campaigns, cinematic scores, fast-paced action, and deep atmosphere.
        </Typography>

        <Typography
          variant="h4"
          align="center"
          color={headerColor}
          sx={{
            fontFamily: 'Light-1',
            textShadow: headerShadow,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
            mb: 1.5
          }}
        >
          Reel
        </Typography>

        <Typography
          color={bodyColor}
          sx={{ fontFamily: '"Courier New", monospace', fontSize: { xs: '1rem', sm: '1.05rem', md: '1.15rem' }, lineHeight: 1.8, mb: 2.5 }}
        >
          Curated selection of music and sound design created for brand-focused storytelling.
        </Typography>

        <MusicPlayer
          song={reelStreamUrl}
          isLoading={reelLoading}
          slug={reel?.slug || REEL_SLUG}
          songId={reel?.id}
          songImage={reelImageUrl}
          fullWidth={true}
          counterPath="/reel-counter/"
          refreshSong={reel?.id ? async () => { await fetchAuth(); return buildReelStreamUrl(reel.reel ?? reel.id); } : undefined}
        />

        {/* ── About Vadym section ── */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 6 }}
          alignItems="flex-start"
          sx={{ mt: 5, mb: 6 }}
        >
          {/* Left: text */}
          <Box sx={{ flex: '1 1 55%', minWidth: 0 }}>
            <Typography variant="h3" color={headerColor} sx={{
                fontFamily: 'Light-1', textShadow: headerShadow,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, lineHeight: 1,
                mb: 3,
              }}>BEHIND THE SOUND</Typography>

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3.5 }}>
              <Box component="img" src={AvatarPhoto} alt="Vadym D"
                sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  objectFit: 'cover', objectPosition: '50% 15%', flexShrink: 0,
                  border: isDark ? '1px solid rgba(242,211,153,0.35)' : '1px solid rgba(0,0,0,0.15)',
                  boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
              <Box>
                <Typography sx={{
                  fontFamily: '"Courier New", monospace', fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: isDark ? 'white' : '#1a1a1a', lineHeight: 1.3,
                }}>Distorise</Typography>
                <Typography sx={{
                  fontFamily: '"Courier New", monospace', fontWeight: 600,
                  fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: isDark ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.48)',
                  lineHeight: 1.4, mt: 0.3,
                }}>Composer &amp; Sound Designer</Typography>
              </Box>
            </Stack>

            <Stack spacing={2.5}>
              <Typography color={bodyColor} sx={{ fontFamily: '"Courier New", monospace', fontWeight: 600, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.8 }}>
                I&apos;m <strong>Vadym</strong>, composing and designing sound under the Distorise project. My work combines electronic production, cinematic composition, and sound design to help brands tell compelling stories through sound.
              </Typography>
              <Typography sx={{
                fontFamily: '"Courier New", monospace', fontWeight: 700,
                fontSize: '0.82rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                color: isDark ? '#F2D399' : '#2f4c4c',
              }}>Based in Ukraine. Working worldwide.</Typography>
            </Stack>
          </Box>

          {/* Right: portrait */}
          <Box sx={{ flex: '1 1 42%', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{
              position: 'relative', width: '100%',
              aspectRatio: '1 / 1',
              borderRadius: 2, overflow: 'hidden',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.12)',
              boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.6)' : '0 8px 28px rgba(0,0,0,0.12)',
            }}>
              <Box component="img" src={PortraitPhoto} alt="Vadym D portrait"
                sx={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: '50% 60%',
                  filter: isDark ? 'brightness(0.68)' : 'brightness(0.82)',
                }}
              />
              <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: [
                  'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 10px)',
                  'repeating-linear-gradient(-45deg, rgba(0,0,0,0.07) 0 1px, transparent 1px 16px)',
                ].join(','),
              }} />
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                px: 2, py: 1.2, background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(4px)',
                display: 'flex', justifyContent: 'center',
              }}>
                <Typography sx={{
                  fontFamily: '"Courier New", monospace', fontWeight: 600,
                  fontSize: '0.7rem', letterSpacing: '0.18em',
                  color: 'rgba(255,255,255,0.62)', textTransform: 'uppercase',
                }}>[ Vadym D ]</Typography>
              </Box>
            </Box>
          </Box>
        </Stack>

        <Typography
          variant="h4"
          align="center"
          color={headerColor}
          sx={{
            fontFamily: 'Light-1',
            textShadow: headerShadow,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
            mt: 5,
            mb: 1.5
          }}
        >
          Rescores
        </Typography>

        <Typography
          sx={{
            fontFamily: '"Courier New", monospace',
            fontWeight: 'bold',
            lineHeight: 1.8,
            mb: 3,
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            borderRadius: 2,
            color: isDark ? '#F2D399' : '#4b3416',
            backgroundColor: isDark ? 'rgba(242, 211, 153, 0.08)' : 'rgba(242, 211, 153, 0.32)',
            border: isDark ? '1px solid rgba(242, 211, 153, 0.28)' : '1px solid rgba(75, 52, 22, 0.18)'
          }}
        >
          Note: The projects below are independent rescoring exercises created for portfolio purposes and are not official collaborations with the featured brands.
        </Typography>

        <Stack spacing={3}>
          {rescoringProjects.map((project) => (
            <Box
              key={project.title}
              sx={{
                position: 'relative',
                border: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.1)',
                boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.25)' : '0 10px 24px rgba(0,0,0,0.12)',
                borderRadius: 2,
                p: { xs: 2.5, md: 3 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: isDark
                    ? 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,0.7) 85%, rgba(0,0,0,0.05) 100%)'
                    : 'linear-gradient(90deg, rgba(255,250,244,0.1) 0%, rgba(255,250,244,0.92) 15%, rgba(255,250,244,0.92) 85%, rgba(255,250,244,0.1) 100%)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 2,
                  zIndex: -1
                }
              }}
            >
              <Typography
                variant="h5"
                color={accentGold}
                sx={{ fontFamily: '"Courier New", monospace', fontWeight: 'bold', mb: 1.2 }}
              >
                {project.title}
              </Typography>

              <Typography color={isDark ? 'white' : '#1a1a1a'} sx={{ fontFamily: '"Courier New", monospace', fontWeight: 600, lineHeight: 1.8, mb: 2 }}>
                {project.description}
              </Typography>

              <VideoEmbed videoId={project.videoId} title={`${project.title} rescore`} isDark={isDark} />
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default WorkForBrands;
